import React, { useEffect, useState } from 'react';
import { Button, Table, Switch, Space, Popconfirm, message } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { customBaseUrl, httpGet, httpPut, httpDelete } from '../../../store/http';
import { hideLoader, showLoader } from '../../../utils/loader';
import Swal from 'sweetalert2';
import CreatePricingPlanModal from './CreatePricingPlanModal';
import EditPricingPlanModal from './EditPricingPlanModal';
import CreatePricingWithCommissionModal from './CreatePricingWithCommissionModal';
import EditPricingWithCommissionModal from './EditPricingWithCommissionModal';
import ViewPricingPlanModal from './ViewPricingPlanModal';

const PricingList = ({ productCode }) => {
  const [pricingPlans, setPricingPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreatePricingPlanModal, setShowCreatePricingPlanModal] = useState(false);
  const [showEditPricingPlanModal, setShowEditPricingPlanModal] = useState(false);
  const [showCreatePricingWithCommissionModal, setShowCreatePricingWithCommissionModal] = useState(false);
  const [showEditPricingWithCommissionModal, setShowEditPricingWithCommissionModal] = useState(false);
  const [selectedPricingPlanToEdit, setSelectedPricingPlanToEdit] = useState(null);
  const [selectedPricingPlanWithCommissionToEdit, setSelectedPricingPlanWithCommissionToEdit] = useState(null);
  const [showViewPricingPlanModal, setShowViewPricingPlanModal] = useState(false);
  const [selectedPricingPlanToView, setSelectedPricingPlanToView] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const fetchPricingPlans = async () => {
    setLoading(true);
    showLoader();
    try {
      const response = await httpGet(`${customBaseUrl.integUrl}/api/v1/pricing/products/product/${productCode}`);
      if (response.status) {
        setPricingPlans(response.data);
        setPagination({
          ...pagination,
          total: response.data.length,
          current: 1,
        });
      } else {
        Swal.fire('Error', response.message || 'Failed to fetch pricing plans', 'error');
      }
    } catch (error) {
      console.error('Error fetching pricing plans:', error);
      Swal.fire('Error', 'An error occurred while fetching pricing plans', 'error');
    } finally {
      hideLoader();
      setLoading(false);
    }
  };

  useEffect(() => {
    if (productCode) {
      fetchPricingPlans();
    }
  }, [productCode]);

  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };

  const handleToggleActive = async (id, active) => {
    showLoader();
    try {
      const response = await httpPut(`${customBaseUrl.integUrl}/api/v1/pricing/products/${id}`, { isActive: !active });
      if (response.status) {
        message.success(`Pricing plan ${active ? 'deactivated' : 'activated'} successfully`);
        fetchPricingPlans();
      } else {
        Swal.fire('Error', response.message || 'Failed to update pricing plan status', 'error');
      }
    } catch (error) {
      console.error('Error toggling pricing plan status:', error);
      Swal.fire('Error', 'An error occurred while updating pricing plan status', 'error');
    } finally {
      hideLoader();
    }
  };

  const handleDeletePricingPlan = async (id) => {
    showLoader();
    try {
      const response = await httpDelete(`${customBaseUrl.integUrl}/api/v1/pricing/products/${id}`);
      if (response.status) {
        message.success('Pricing plan deleted successfully');
        fetchPricingPlans();
      } else {
        Swal.fire('Error', response.message || 'Failed to delete pricing plan', 'error');
      }
    } catch (error) {
      console.error('Error deleting pricing plan:', error);
      Swal.fire('Error', 'An error occurred while deleting pricing plan', 'error');
    } finally {
      hideLoader();
    }
  };

  const handleEditPricingPlan = (record) => {
    if (record.commissionActive !== undefined) { // Assuming commissionActive indicates a commission plan
      setSelectedPricingPlanWithCommissionToEdit(record);
      setShowEditPricingWithCommissionModal(true);
    } else {
      setSelectedPricingPlanToEdit(record);
      setShowEditPricingPlanModal(true);
    }
  };

  const handleViewPricingPlan = (record) => {
    setSelectedPricingPlanToView(record);
    setShowViewPricingPlanModal(true);
  };

  const columns = [
    {
      title: 'Pricing Name',
      dataIndex: 'pricingName',
      key: 'pricingName',
    },
    {
      title: 'Pricing Model',
      dataIndex: 'pricingModel',
      key: 'pricingModel',
    },
    {
      title: 'Base Price',
      dataIndex: 'basePrice',
      key: 'basePrice',
      render: (text, record) => `${record.currency} ${text}`,
    },
    {
      title: 'Effective From',
      dataIndex: 'effectiveFrom',
      key: 'effectiveFrom',
      render: (text) => new Date(text).toLocaleDateString(),
    },
    {
      title: 'Effective To',
      dataIndex: 'effectiveTo',
      key: 'effectiveTo',
      render: (text) => new Date(text).toLocaleDateString(),
    },
    {
      title: 'Active',
      dataIndex: 'active',
      key: 'active',
      render: (active, record) => (
        <Switch
          checked={active}
          onChange={() => handleToggleActive(record.id, active)}
        />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (text, record) => (
        <Space size="middle">
          <Button onClick={() => handleViewPricingPlan(record)}>View Details</Button>
          <Button icon={<EditOutlined />} onClick={() => handleEditPricingPlan(record)} />
          <Popconfirm
            title="Are you sure to delete this pricing plan?"
            onConfirm={() => handleDeletePricingPlan(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button icon={<DeleteOutlined />} danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, textAlign: 'right' }}>
        <Space>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setShowCreatePricingPlanModal(true)}>
            Create Pricing Plan
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setShowCreatePricingWithCommissionModal(true)}>
            Create Pricing with Commission
          </Button>
        </Space>
      </div>
      <Table
        columns={columns}
        dataSource={pricingPlans}
        rowKey="id"
        loading={loading}
        pagination={pagination}
        onChange={handleTableChange}
      />
      <CreatePricingPlanModal
        showModal={showCreatePricingPlanModal}
        hideModal={() => setShowCreatePricingPlanModal(false)}
        reloadPricingPlans={fetchPricingPlans}
        productCode={productCode}
      />
      <EditPricingPlanModal
        showModal={showEditPricingPlanModal}
        hideModal={() => setShowEditPricingPlanModal(false)}
        reloadPricingPlans={fetchPricingPlans}
        productCode={productCode}
        pricingPlan={selectedPricingPlanToEdit}
      />
      <CreatePricingWithCommissionModal
        showModal={showCreatePricingWithCommissionModal}
        hideModal={() => setShowCreatePricingWithCommissionModal(false)}
        reloadPricingPlans={fetchPricingPlans}
        productCode={productCode}
      />
      <EditPricingWithCommissionModal
        showModal={showEditPricingWithCommissionModal}
        hideModal={() => setShowEditPricingWithCommissionModal(false)}
        reloadPricingPlans={fetchPricingPlans}
        productCode={productCode}
        pricingPlan={selectedPricingPlanWithCommissionToEdit}
      />
      <ViewPricingPlanModal
        showModal={showViewPricingPlanModal}
        hideModal={() => setShowViewPricingPlanModal(false)}
        pricingPlan={selectedPricingPlanToView}
      />
    </div>
  );
};

export default PricingList;
